import React, { useCallback, useMemo } from 'react';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import DataTable from '../../components/common/DataTable/DataTable';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import ExpandableDetails from '../../components/common/ExpandableDetails/ExpandableDetails';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import SingleSelectFilter from '../../components/common/SingleSelectFilter/SingleSelectFilter';
import SysBadge from '../../components/common/SysListing/SysBadge';
import styles from '../../components/common/SysListing/SysListing.module.css';
import {
  SYS_DATE_PRESET_OPTIONS,
  getSysDateDefaultRange,
  isSysDateDefaultRange,
} from '../../constants/datePresets';
import { usePageSize } from '../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { useSystemLogs } from '../../hooks/useSystemLogs';
import {
  SYSTEM_LOG_LEVEL_LABELS,
  SYSTEM_LOG_LEVEL_OPTIONS,
  SYSTEM_LOG_SYSTEM_LABELS,
  SYSTEM_LOG_SYSTEM_OPTIONS,
  getSystemLogError,
  getSystemLogErrorDetails,
  getSystemLogSentPayload,
  type SystemLogFilters,
  type SystemLogLevel,
  type SystemLogSystem,
} from '../../types/systemLog';
import { formatDateTime } from '../../utils';

const DEFAULT_DATE_RANGE = getSysDateDefaultRange();
const LOG_FILTER_SCHEMA = {
  page: queryFilter.integer(1, { min: 1 }),
  search: queryFilter.string(),
  level: queryFilter.enum<SystemLogLevel | ''>('', ['', ...SYSTEM_LOG_LEVEL_OPTIONS.map(({ value }) => value)]),
  system: queryFilter.enum<SystemLogSystem | ''>('', ['', ...SYSTEM_LOG_SYSTEM_OPTIONS.map(({ value }) => value)]),
  dateFrom: queryFilter.string(DEFAULT_DATE_RANGE.start),
  dateTo: queryFilter.string(DEFAULT_DATE_RANGE.end),
};

const Logs: React.FC = () => {
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('system_logs_page_size', 10);
  const { filters: queryFilters, setFilter, setFilters, resetFilters } = useQueryFilters(
    LOG_FILTER_SCHEMA,
    { pageKey: 'page' },
  );
  const {
    search,
    level: selectedLevel,
    system: selectedSystem,
    dateFrom,
    dateTo,
  } = queryFilters;

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleDateChange = useCallback((start: string, end: string) => {
    setFilters({ dateFrom: start, dateTo: end });
  }, [setFilters]);

  const filters: SystemLogFilters = useMemo(() => ({
    search: search || undefined,
    logLevel: selectedLevel ? selectedLevel as SystemLogLevel : undefined,
    logSystem: selectedSystem ? selectedSystem as SystemLogSystem : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [dateFrom, dateTo, search, selectedLevel, selectedSystem]);

  const { rows, pagination, loading, error } = useSystemLogs(page, pageSize, filters);
  const hasActiveFilters = !!search
    || !!selectedLevel
    || !!selectedSystem
    || !isSysDateDefaultRange(dateFrom, dateTo);

  return (
    <PageContainer className={styles.page}>
      <FilterBar gap="sm" pushFirstChild>
        <RecordCount
          label="Logs:"
          count={`${pagination.totalRecords} registros`}
          className={styles['record-count']}
        />
        <FilterGroup>
          <SearchFilterBar
            searchValue={search}
            onSearchChange={(value) => setFilter('search', value)}
            searchPlaceholder="Mensagem, função, operação ou request id"
            loading={loading}
          />
          <DatePresetFilter
            dateStart={dateFrom}
            dateEnd={dateTo}
            onChange={handleDateChange}
            placeholder="Período"
            presetOptions={SYS_DATE_PRESET_OPTIONS}
          />
          <SingleSelectFilter
            value={selectedLevel}
            onChange={(value) => setFilter('level', value as SystemLogLevel | '')}
            options={SYSTEM_LOG_LEVEL_OPTIONS}
            placeholder="Tipo"
          />
          <SingleSelectFilter
            value={selectedSystem}
            onChange={(value) => setFilter('system', value as SystemLogSystem | '')}
            options={SYSTEM_LOG_SYSTEM_OPTIONS}
            placeholder="Sistema"
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
      </FilterBar>

      {loading ? (
        <Loading />
      ) : error ? (
        <EmptyState message={`Erro: ${error}`} />
      ) : rows.length === 0 ? (
        <EmptyState message="Nenhum log encontrado com os filtros selecionados." />
      ) : (
        <DataTable
          headers={[
            'Data e hora',
            'Tipo',
            'Sistema',
            'Função',
            'Mensagem',
          ]}
          gridTemplate="160px 90px 90px 160px 1fr"
          highlightTerm={search}
          lastColumnIsActions={false}
        >
          {rows.map((row) => {
            const sentPayload = getSystemLogSentPayload(row);
            const functionLabel = [row.function_name, row.operation].filter(Boolean).join(' · ') || '—';

            return (
              <DataTable.Row key={row.id}>
                <div className={styles['date-cell']}>
                  <span className={styles['date-text']}>{formatDateTime(row.occurred_at)}</span>
                </div>

                <div className={styles['type-cell']}>
                  <SysBadge tone={row.log_level}>
                    {SYSTEM_LOG_LEVEL_LABELS[row.log_level]}
                  </SysBadge>
                </div>

                <div className={styles['system-cell']}>
                  <SysBadge tone={row.log_system}>
                    {SYSTEM_LOG_SYSTEM_LABELS[row.log_system]}
                  </SysBadge>
                </div>

                <div className={styles['function-cell']}>
                  <span className={styles['function-text']} title={row.request_id ?? undefined}>
                    {functionLabel}
                  </span>
                </div>

                <div className={styles['message-cell']}>
                  <ExpandableDetails
                    summary={getSystemLogError(row)}
                    sections={[
                      { label: 'Erro', content: getSystemLogErrorDetails(row) },
                      sentPayload ? {
                        label: 'Payload',
                        content: JSON.stringify(sentPayload, null, 2),
                      } : null,
                    ]}
                  />
                </div>
              </DataTable.Row>
            );
          })}
        </DataTable>
      )}

      {rows.length > 0 && (
        <ListingBottomRow
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={handlePageSizeChange}
          pagination={pagination}
          loading={loading}
          onPageChange={handlePageChange}
        />
      )}
    </PageContainer>
  );
};

export default Logs;
